import React, { Component } from "react";
import { Alert, Modal, Upload, Row, Col, Tag, Tooltip, Table, message } from "antd";
import { UploadOutlined, DownloadOutlined, WarningOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx-js-style";
import { ButtonItem } from "../../../../Components/Items";
import { descargarExcelEdicion, analizarLibroEdicion, aplicarCambiosEdicion } from "./excelEdicionAguinaldo";

const HOJA_PREFERIDA = "Aguinaldo";

/**
 * Editar el borrador de aguinaldo en Excel: se descarga la tabla del paso Empleados, se corrige y
 * se vuelve a subir. Antes de aplicar se enseña qué cambia en cada empleado.
 *
 * Es un modal (como ModalImportarIncidenciasExcel) porque el archivo se cruza contra el borrador
 * abierto en el asistente, y no se puede perder. La lectura y las reglas viven en
 * excelEdicionAguinaldo.js; aquí sólo se orquesta. Aplicar sólo cambia la tabla: el asistente
 * guarda el borrador después.
 */
class ModalExcelAguinaldo extends Component {
  state = { archivos: [], analisis: null, procesando: false, aplicando: false };

  componentDidUpdate(prevProps) {
    if (!prevProps.visible && this.props.visible) {
      this.setState({ archivos: [], analisis: null, procesando: false, aplicando: false });
    }
  }

  descargar = () => {
    const p = this.props;
    try {
      descargarExcelEdicion({
        empleados: p.empleados,
        diasAnio: p.diasAnio,
        ejercicio: p.ejercicio,
        tipoAplicacion: p.tipoAplicacion,
        descontarIncidencias: p.descontarIncidencias,
        nombrePrenomina: p.nombrePrenomina,
        empresaNombre: p.empresaNombre,
      });
    } catch (err) {
      console.error("[aguinaldo] Excel de edición", err);
      message.error("No se pudo generar el Excel");
    }
  };

  onSubirArchivo = ({ file, onSuccess }) => {
    this.setState({ procesando: true, analisis: null });
    const lector = new FileReader();
    lector.readAsArrayBuffer(file);
    lector.onloadend = (ev) => {
      try {
        const libro = XLSX.read(new Uint8Array(ev.target.result), { type: "array" });
        const nombreHoja = libro.SheetNames.includes(HOJA_PREFERIDA) ? HOJA_PREFERIDA : libro.SheetNames[0];
        const filas = XLSX.utils.sheet_to_json(libro.Sheets[nombreHoja], { header: 1, raw: true, defval: null });
        const analisis = analizarLibroEdicion({
          filas,
          empleados: this.props.empleados,
          diasAnio: this.props.diasAnio,
          descontarIncidencias: this.props.descontarIncidencias,
        });
        if (analisis.error) message.error(analisis.error);
        this.setState({
          procesando: false,
          analisis: analisis.error ? null : analisis,
          archivos: [{ uid: "1", name: file.name, status: analisis.error ? "error" : "done" }],
        });
        if (onSuccess) onSuccess("ok");
      } catch (err) {
        console.error("[aguinaldo] leer Excel", err);
        message.error("No se pudo leer el archivo. Verifica que sea un .xlsx válido.");
        this.setState({ procesando: false, archivos: [] });
      }
    };
  };

  aplicar = async () => {
    const { analisis } = this.state;
    if (!analisis?.resumen.conCambios) return;
    this.setState({ aplicando: true });
    await this.props.onAplicar(aplicarCambiosEdicion(this.props.empleados, analisis));
    this.setState({ aplicando: false });
  };

  columnas() {
    return [
      { title: "Fila", dataIndex: "filaExcel", width: 60, align: "center" },
      { title: "Empleado", dataIndex: "nombre", width: 230, ellipsis: true },
      {
        title: "Cambios",
        key: "cambios",
        render: (_, r) => (r.cambios.length === 0
          ? <span className="aguinaldo-tenue">—</span>
          : r.cambios.map((c) => (
            <Tag key={c.campo} color="blue" style={{ marginBottom: 2 }}>
              {c.campo}: {c.antes} → <b>{c.despues}</b>
            </Tag>
          ))),
      },
      {
        title: "Estado",
        key: "estado",
        width: 240,
        render: (_, r) => {
          if (r.estado === "error") {
            return (
              <Tooltip title={r.errores.join(" · ")}>
                <span className="aguinaldo-error-texto"><WarningOutlined /> {r.errores[0]}</span>
              </Tooltip>
            );
          }
          return (
            <div>
              {r.estado === "cambia" ? <Tag color="green">Se actualiza</Tag> : <span className="aguinaldo-tenue">Sin cambios</span>}
              {r.avisos.map((a) => <div key={a} className="aguinaldo-aviso-texto">{a}</div>)}
            </div>
          );
        },
      },
    ];
  }

  render() {
    const { visible, onCerrar, descontarIncidencias } = this.props;
    const { archivos, analisis, procesando, aplicando } = this.state;
    const resumen = analisis?.resumen;
    const conCambios = resumen?.conCambios || 0;

    return (
      <Modal
        title="Editar el aguinaldo en Excel"
        visible={visible}
        onCancel={onCerrar}
        width={1000}
        footer={[
          <ButtonItem key="cancelar" onClick={onCerrar}>Cancelar</ButtonItem>,
          <ButtonItem
            key="aplicar"
            type="primary"
            disabled={!conCambios || procesando}
            loading={aplicando}
            onClick={this.aplicar}
          >
            {conCambios ? `Aplicar ${conCambios} cambio(s)` : "Aplicar"}
          </ButtonItem>,
        ]}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Descarga, edita y vuelve a subir"
          description={(
            <span>
              El Excel trae a todos los empleados del borrador. Edita sólo las columnas en amarillo:
              <b> Incluir</b> (Sí/No){descontarIncidencias && <>, <b>Incidencias</b></>}, <b>Días trabajados</b> e{" "}
              <b>Importe</b>; no cambies la columna <b>Id</b>. Igual que en la tabla:{" "}
              {descontarIncidencias && "cambiar las incidencias recalcula los días trabajados; "}
              cambiar los días regresa el importe a la fórmula, y un importe distinto de la fórmula queda como
              manual. Los empleados que borres del archivo se quedan como están.
            </span>
          )}
        />

        <Row gutter={16} align="bottom" style={{ marginBottom: 16 }}>
          <Col xs={24} sm={10}>
            <div className="aguinaldo-tenue" style={{ marginBottom: 4 }}>1. Excel del borrador</div>
            <ButtonItem celeste icon={<DownloadOutlined />} onClick={this.descargar}>
              Descargar Excel
            </ButtonItem>
          </Col>
          <Col xs={24} sm={14}>
            <div className="aguinaldo-tenue" style={{ marginBottom: 4 }}>2. Archivo editado</div>
            <Upload
              showUploadList={{ showRemoveIcon: false, showPreviewIcon: false }}
              fileList={archivos}
              customRequest={this.onSubirArchivo}
              accept=".xlsx"
            >
              <ButtonItem icon={<UploadOutlined />} loading={procesando}>Subir Excel</ButtonItem>
            </Upload>
          </Col>
        </Row>

        {resumen && (
          <Alert
            type={resumen.conError ? "warning" : "success"}
            showIcon
            style={{ marginBottom: 12 }}
            message={(
              <>
                <b>{resumen.conCambios}</b> empleado(s) con cambios · {resumen.sinCambios} sin cambios
                {resumen.conError > 0 && <> · <b>{resumen.conError}</b> con error (se omiten)</>}
                {resumen.noVienen > 0 && <> · {resumen.noVienen} no vienen en el archivo</>}
              </>
            )}
            description={analisis.avisos.length ? analisis.avisos.join(" ") : undefined}
          />
        )}

        {analisis && (
          <Table
            size="small"
            rowKey={(r) => String(r.filaExcel)}
            dataSource={analisis.renglones.filter((r) => r.estado !== "igual" || r.avisos.length)}
            columns={this.columnas()}
            pagination={false}
            scroll={{ y: 360 }}
            locale={{ emptyText: "El archivo no trae cambios" }}
          />
        )}
      </Modal>
    );
  }
}

export default ModalExcelAguinaldo;
